// Bills and payments (ERP-PLAN §5.9), spread into `mutations` (shared/mutations.ts): bills and their lines, bills
// made from a PO (deposit, balance, what's been received), approving and voiding, accepting 3-way match differences,
// recording a payment made outside HHL Ops, linking a Wise history transfer to what it paid, and a vendor's Wise
// recipient. Rules and implementations: server/src/mutations/bills.ts. Creating a Wise transfer isn't a mutation: it
// calls Wise (POST /api/wise/pay, server/src/wise/routes.ts). Recording or linking payments and linking recipients
// need `app_user.can_pay` as well as the role (403 otherwise).
import { z } from "zod";
import { ADJUSTMENT_KINDS, billFields, billLineFields, billLineInput, MATCH_TARGET_KINDS, PAYMENT_METHODS, PO_BILL_PARTS, WISE_REASONS } from "./bills.ts";
import { create, custom, remove, update } from "./mutation-kit.ts";
import { PAID_VIA, payAccountFields } from "./pay-accounts.ts";

const isoDate = z.iso.date("Use a date like 2026-12-31");
const money = z.number().positive("An amount is more than 0").max(100_000_000);

export const billMutations = {
  /**
   * A bill (the browser makes the id; the same id twice is a no-op), with its lines: PO lines, a PO's deposit, a
   * run's cost, a tooling item. Starts as a draft unless `approve`. The invoice file is attached after
   * (owner_type bill). Returns { id, number }.
   */
  "bill.create": custom(
    billFields
      .partial()
      .required({ vendor_id: true, amount: true })
      .extend({ id: z.uuid(), lines: z.array(billLineInput).max(500).default([]), approve: z.boolean().optional() }),
  ),
  /**
   * A bill for part of a PO (shared/bills.ts billFromPo()): its deposit (deposit_pct), the balance (every line not
   * billed yet, less deposits billed), or what's been received and not billed. Returns { id, number }.
   */
  "bill.create_from_po": custom(
    z.object({
      id: z.uuid(),
      po_id: z.uuid(),
      part: z.enum(PO_BILL_PARTS),
      invoice_number: z.string().trim().max(100).optional(),
      bill_date: isoDate.nullable().optional(),
      due_date: isoDate.nullable().optional(),
      approve: z.boolean().optional(),
    }),
  ),
  "bill.update": update("bill", billFields),
  /** Only a draft with no payments; void the others. */
  "bill.remove": remove("bill"),
  /** draft → approved (checked and owed), back to draft, void (not owed; never while a payment holds it), void → draft. */
  "bill.set_status": custom(z.object({ id: z.uuid(), status: z.enum(["draft", "approved", "void"]), note: z.string().trim().max(500).optional() })),
  /** Accepts the 3-way match differences shown (`key` = billMatch().key) with why; a new difference shows again. */
  "bill.accept_match": custom(z.object({ id: z.uuid(), key: z.string().min(1).max(64), note: z.string().trim().min(1, "Say why it's OK").max(1000) })),
  "bill.clear_match": custom(z.object({ id: z.uuid() })),
  "bill_line.create": create("bill_line", billLineInput.omit({ id: true }).extend({ bill_id: z.uuid(), position: z.number().int().min(0).max(100_000) })),
  "bill_line.update": update("bill_line", billLineFields),
  "bill_line.remove": remove("bill_line"),

  /**
   * A payment made outside HHL Ops (card, wire, other) or a Wise transfer made in the Wise app, already gone: paid on
   * `paid_on`, split over the bills (each its amount). Bills it covers become paid, and the run costs and tooling they
   * pay are marked paid. Needs can_pay. Its receipt is attached after (owner_type payment). Returns { id, number }.
   */
  "payment.record": custom(
    z.object({
      id: z.uuid(),
      method: z.enum(PAYMENT_METHODS),
      paid_on: isoDate,
      reference: z.string().trim().max(200).default(""),
      /** A transfer made in the Wise app (method wise): its number, so webhooks follow it and its receipt comes. */
      wise_transfer_id: z.number().int().positive().max(1e15).nullable().optional(),
      fee: z.number().min(0).max(1_000_000).default(0),
      notes: z.string().trim().max(5000).default(""),
      /** Which card --- the account that paid (required for a card) and where it was paid (migration 073). */
      pay_account_id: z.uuid().nullable().optional(),
      paid_via: z.enum(["", ...PAID_VIA]).optional(),
      bills: z
        .array(z.object({ bill_id: z.uuid(), amount: money }))
        .min(1, "Pick the bills it paid")
        .max(100),
    }),
  ),
  "payment.update": update(
    "payment",
    z.object({
      notes: z.string().trim().max(5000),
      reference: z.string().trim().max(200),
      pay_account_id: z.uuid().nullable(), // Which card ---
      paid_via: z.enum(["", ...PAID_VIA]),
    }),
  ),
  /** Which card --- HHL's own ways to pay: a name and the last 4 digits only (migration 073). Needs Can pay. */
  "pay_account.create": create("pay_account", payAccountFields.partial().required({ name: true, kind: true })),
  "pay_account.update": update("pay_account", payAccountFields),
  /**
   * Takes a payment back: one recorded by mistake (outside, or linked from Wise history), or a Wise one that never
   * reached Wise (not in Wise yet, or refused). One in Wise is cancelled in Wise (its webhook marks it here). Needs can_pay.
   */
  "payment.remove": custom(z.object({ id: z.uuid() })),
  /** A fee that may be one entered on the run by hand (fee_review): add it to the run, or it's that one. */
  "payment.fee_decide": custom(z.object({ id: z.uuid(), add: z.boolean() })),

  /**
   * Links a transfer from Wise history to what it paid: bills, or a PO (a bill is made for that part), a PO without
   * its price yet (`po_unpriced`: the payment sets its one line's price, then it's billed), a run's cost or a tooling
   * item (a bill is made for it); each amount up to what it owes (less is a partial payment). `adjustments`: the
   * vendor's rounding ($1.00 at most, a `rounding` line on the largest bill paid in full), or what HHL paid extra (a
   * bank charge, shipping, other: a run cost on `run_id` when something picked is on that run, else a charge on the
   * largest bill). Targets and adjustments add up to the transfer, to the cent (shared/bills.ts planLink()). Makes
   * the paid Wise payment (fee and all). Needs can_pay.
   */
  "wise_transfer.link": custom(
    z.object({
      id: z.uuid(),
      payment_id: z.uuid(),
      targets: z
        .array(
          z.object({
            kind: z.enum(MATCH_TARGET_KINDS),
            id: z.uuid(),
            part: z.enum(PO_BILL_PARTS).optional(),
            amount: money,
          }),
        )
        .min(1, "Pick what it paid")
        .max(20),
      adjustments: z
        .array(
          z.object({
            kind: z.enum(ADJUSTMENT_KINDS),
            amount: z.number().min(-100_000_000).max(100_000_000),
            description: z.string().trim().max(200).default(""),
            run_id: z.uuid().nullable().optional(),
          }),
        )
        .max(5)
        .default([]),
    }),
  ),
  /** Not a vendor payment (a contractor, a refund…): left out of matching. */
  "wise_transfer.ignore": custom(
    z.object({
      id: z.uuid(),
      ignored: z.boolean(),
      /** Accounted for --- why it isn't a vendor bill (required to set it aside; migration 074), and a note. */
      reason: z.enum(WISE_REASONS).optional(),
      note: z.string().trim().max(500).optional(),
    }),
  ),

  /** Forgets a vendor's Wise recipient (Wise keeps it). Linking one goes through Wise: POST /api/wise/recipients/link. Needs can_pay. */
  "vendor_recipient.remove": remove("vendor_recipient"),
} as const;
